import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, Legend,
} from "recharts";
import { TrendingUp } from "lucide-react";
import { qrtOverview, qrtTimeseries, qrtTopAccounts } from "../../apis/qrtAdmin";
import { StatCard, Spinner, Pager, num, fmtMoney } from "./qrtUi";

const TOP_ACCOUNTS_PAGE = 5;

const QrtOverview = () => {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [series, setSeries] = useState(null);
  const [days, setDays] = useState(30);
  const [error, setError] = useState("");
  const [rangeLoading, setRangeLoading] = useState(false);
  const [accounts, setAccounts] = useState(null);
  const [accountsPage, setAccountsPage] = useState(0);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState("");

  useEffect(() => {
    qrtOverview()
      .then((res) => setData(res.data?.data))
      .catch((err) => setError(err.response?.data?.error || "Failed to load overview"));
  }, []);

  useEffect(() => {
    let cancelled = false;
    setAccountsLoading(true);
    setAccountsError("");
    qrtTopAccounts(accountsPage)
      .then((res) => {
        if (!cancelled) setAccounts(res.data?.data || { rows: [], total: 0 });
      })
      .catch((err) => {
        if (!cancelled) setAccountsError(err.response?.data?.error || "Failed to load accounts");
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false);
      });
    return () => { cancelled = true; };
  }, [accountsPage]);

  useEffect(() => {
    let cancelled = false;
    setRangeLoading(true);
    qrtTimeseries(days)
      .then((ts) => {
        if (!cancelled) setSeries(ts.data?.data);
      })
      .catch(() => {
        if (!cancelled) setError("Failed to load period data");
      })
      .finally(() => {
        if (!cancelled) setRangeLoading(false);
      });
    return () => { cancelled = true; };
  }, [days]);

  if (error && !data) return <div className="ping-banner red">{error}</div>;
  if (!data) return <Spinner />;

  const m = data.messages;
  const c = data.campaigns.by_status || {};
  const t = data.templates.by_status || {};
  const periodMsgs = (series?.messages || []).reduce(
    (acc, r) => ({ sent: acc.sent + Number(r.sent || 0), received: acc.received + Number(r.received || 0) }),
    { sent: 0, received: 0 }
  );
  const periodRev = (series?.revenue || []).reduce(
    (acc, r) => ({
      direct: acc.direct + Number(r.direct_revenue ?? r.revenue ?? 0),
      take: acc.take + Number(r.partner_take ?? 0),
    }),
    { direct: 0, take: 0 }
  );

  const msgChart = (series?.messages || []).map((r) => ({
    date: String(r.date).slice(5),
    Sent: Number(r.sent),
    Received: Number(r.received),
  }));
  const revChart = (series?.revenue || []).map((r) => ({
    date: String(r.date).slice(5),
    "Direct revenue": Number(r.direct_revenue ?? r.revenue ?? 0),
    "Partner take": Number(r.partner_take ?? 0),
    Recharges: Number(r.recharges ?? 0),
  }));

  return (
    <div>
      <div className="qrt-page-head">
        <div>
          <h2>Platform overview</h2>
          <p>BluTec Ping admin · figures refresh every 60 seconds</p>
        </div>
        <div className="grow" />
        <div className="qrt-inline">
          <button type="button" className="ping-btn ping-btn-ghost" onClick={() => navigate("/qrt/revenue")}>
            <TrendingUp size={14} />
            Blutec revenue
          </button>
          <select value={days} onChange={(e) => setDays(Number(e.target.value))} disabled={rangeLoading}>
            <option value={7}>Last 7 days</option>
            <option value={30}>Last 30 days</option>
            <option value={90}>Last 90 days</option>
          </select>
        </div>
      </div>

      <div className="qrt-stats">
        <StatCard label="Users" value={num(data.users.owners)}
          sub={`${num(data.users.agents)} agents · ${num(data.users.suspended)} deactivated`} />
        <StatCard label="New users (30d)" value={num(data.users.new_30d)} sub={`${num(data.users.new_7d)} in last 7 days`} />
        <StatCard label="WABA connections" value={num(data.waba.accounts)}
          sub={`${num(data.waba.wabas)} WABAs · ${num(data.waba.users)} users`} tone="blue" />
        <StatCard label="Campaigns" value={num(data.campaigns.total)}
          sub={`${num(c.active || 0)} active · ${num(c.completed || 0)} completed`} />
        <StatCard label="Chatbot flows" value={num(data.flows.total)} sub={`${num(data.flows.active)} active`} />
        <StatCard label="Templates" value={num(data.templates.total)}
          sub={`${num(t.APPROVED || t.approved || 0)} approved`} />
      </div>

      <div className="qrt-load-wrap">
        {rangeLoading && (
          <div className="qrt-load-mask" aria-live="polite">
            <Spinner />
          </div>
        )}

        <div className="qrt-stats">
          <StatCard label={`Messages (${days}d)`} value={num(periodMsgs.sent)}
            sub={`${num(periodMsgs.received)} received`} />
          <StatCard label={`Direct revenue (${days}d)`} value={fmtMoney(periodRev.direct)}
            sub={`partner take ${fmtMoney(periodRev.take)}`} />
          <StatCard label="Messages today" value={num(m.today.sent)}
            sub={`${num(m.today.received)} received · ${num(m.today.failed)} failed`} />
          <StatCard label="Wallet float (ours)" value={fmtMoney(data.wallets.direct_float ?? data.wallets.total_float)}
            sub={`direct customers · partner-customer float ${fmtMoney(data.wallets.partner_customer_float)} (not ours)`} tone="blue" />
          <StatCard label="Blutec revenue today" value={fmtMoney(data.billing.today.revenue)}
            sub={`direct ${fmtMoney(data.billing.today.direct_revenue)} · partners ${fmtMoney(data.billing.today.partner_take)}`} />
          <StatCard label="Blutec revenue (month)" value={fmtMoney(data.billing.month.revenue)}
            sub={`direct ${fmtMoney(data.billing.month.direct_revenue)} · partners ${fmtMoney(data.billing.month.partner_take)} · margin ${fmtMoney(data.billing.month.margin)}`} />
        </div>

        <div className="qrt-charts">
          <div className="qrt-chart-card">
            <h3>Message volume (sent / received)</h3>
            <ResponsiveContainer width="100%" height={230}>
              <AreaChart data={msgChart} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Area type="monotone" dataKey="Sent" stroke="#16A34A" fill="#DCFCE7" strokeWidth={2} />
                <Area type="monotone" dataKey="Received" stroke="#0064E0" fill="#E7F3FF" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <div className="qrt-chart-card">
            <h3>Blutec revenue (₹) — direct + partner take</h3>
            <ResponsiveContainer width="100%" height={230}>
              <BarChart data={revChart} margin={{ top: 4, right: 8, left: -14, bottom: 0 }}>
                <XAxis dataKey="date" tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fontSize: 11 }} tickLine={false} axisLine={false} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="Direct revenue" stackId="rev" fill="#16A34A" radius={[0, 0, 0, 0]} />
                <Bar dataKey="Partner take" stackId="rev" fill="#7C3AED" radius={[3, 3, 0, 0]} />
                <Bar dataKey="Recharges" fill="#91D5FF" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

      </div>

      <div className="qrt-card">
        <div className="qrt-card-head">
          <h3>Top accounts by customer spend</h3>
          <span className="sub" style={{ fontSize: 12 }}>
            Partner customers' spend settles to the partner — our cut comes from the partner wallet
          </span>
        </div>
        <table className="qrt-table">
          <thead>
            <tr>
              <th>User</th>
              <th>Workspace</th>
              <th className="r">Customer spend</th>
              <th className="r">Our margin</th>
              <th className="r">Recharges</th>
              <th className="r">Msgs billed</th>
            </tr>
          </thead>
          <tbody>
            {accountsLoading && (
              <tr><td className="empty" colSpan={6}><Spinner /></td></tr>
            )}
            {!accountsLoading && (accounts?.rows || []).map((u) => (
              <tr key={u.id} className="click" onClick={() => navigate(`/qrt/users/${u.id}`)}>
                <td>
                  <div style={{ fontWeight: 600 }}>{u.name}</div>
                  <div className="sub">{u.email}</div>
                </td>
                <td>
                  {u.partner_id ? (
                    <span className="ping-chip p">{u.partner_name || `Partner #${u.partner_id}`}</span>
                  ) : (
                    <span className="ping-chip n">Blutec Ping</span>
                  )}
                </td>
                <td className="r" style={{ fontWeight: 700 }}>{fmtMoney(u.revenue)}</td>
                <td className="r">{u.partner_id ? <span className="sub">via partner wallet</span> : fmtMoney(u.margin)}</td>
                <td className="r">{fmtMoney(u.recharges)}</td>
                <td className="r">{num(u.messages_billed)}</td>
              </tr>
            ))}
            {!accountsLoading && accountsError && (
              <tr><td className="empty" colSpan={6}>{accountsError}</td></tr>
            )}
            {!accountsLoading && !accountsError && (!accounts?.rows || accounts.rows.length === 0) && (
              <tr><td className="empty" colSpan={6}>No billing activity yet</td></tr>
            )}
          </tbody>
        </table>
        <Pager
          page={accountsPage}
          total={accounts?.total || 0}
          pageSize={accounts?.page_size || TOP_ACCOUNTS_PAGE}
          onPage={setAccountsPage}
        />
      </div>

    </div>
  );
};

export default QrtOverview;
